import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { UserProfileService } from "app/core/services/user-profile/user-profile.service";
import { Subject, of, combineLatest } from "rxjs";
import { takeUntil, switchMap, catchError, finalize, tap, filter } from "rxjs/operators";
import { AccountService } from "app/core/services/account/account.service";
import {
  FuseConfirmationConfig,
  FuseConfirmationService,
} from "@fuse/services/confirmation";
import { AuthorityVerificationFileService } from "app/core/services/authority-verification/authority-verification-file.service";

@Component({
  selector: "app-create-account-modal",
  templateUrl: "./create-account-modal.component.html",
})
export class CreateAccountModalComponent implements OnInit, OnDestroy {
  abnInfo;
  viewAccountInformation = true;
  viewAutorityVerification = false;
  viewVerifyingNow = false;
  currentUser;
  account: any;
  profile: any;
  newlyCreatedAccountUuid: string | null = null;

  openAuthorityVerificationMode = false;

  selectedFile: File | null = null;
  uploadProgress: number | undefined;
  disableConfirm = false;
  private unsubscribeAll = new Subject<any>();

  constructor(
    public userProfileService: UserProfileService,
    public dialogRef: MatDialogRef<CreateAccountModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private accountService: AccountService,
    private authorityVerificationFileService: AuthorityVerificationFileService,
    private _fuseConfirmationService: FuseConfirmationService
  ) {
    if (data) {
      this.currentUser = data.currentUser;
      if (data.openAuthorityVerification && data.account) {
        this.openAuthorityVerificationMode = true;
        this.account = data.account;
        this.profile = data.profile ?? this.userProfileService.currentUser.getValue();
        this.viewAccountInformation = false;
        this.viewAutorityVerification = true;
        this.viewVerifyingNow = false;
      }
    }
  }

  ngOnInit(): void {
    combineLatest({
      account: this.accountService.currentAccount$,
      user: this.userProfileService.currentUser
    })
      .pipe(
        takeUntil(this.unsubscribeAll)
      )
      .subscribe(({ account, user }) => {
        if (account && !this.newlyCreatedAccountUuid && !this.openAuthorityVerificationMode) {
          this.account = account;
        }
        if (user && !this.openAuthorityVerificationMode) {
          this.profile = user;
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }

  cancel() {
    this.dialogRef.close();
  }

  save(e) {
    this.dialogRef.close(e);
  }

  setAbnInfo(e) {
    this.abnInfo = e;
  }

  goToNextPage(event) {
    if (typeof event === 'object' && event.step === "authority-verification") {
      this.newlyCreatedAccountUuid = event.accountUuid || null;
      if (this.newlyCreatedAccountUuid) {
        this.accountService.getAccountInformation(this.newlyCreatedAccountUuid)
          .pipe(takeUntil(this.unsubscribeAll))
          .subscribe({
            next: (accountData: any) => {
              this.account = accountData;
              this.viewAutorityVerification = true;
              this.viewAccountInformation = false;
              this.viewVerifyingNow = false;
            },
            error: (error) => {
              console.error('Error fetching account information:', error);
              this.viewAutorityVerification = true;
              this.viewAccountInformation = false;
              this.viewVerifyingNow = false;
            }
          });
      } else {
        this.viewAutorityVerification = true;
        this.viewAccountInformation = false;
        this.viewVerifyingNow = false;
      }
    } else if (event === "verifying-now" || (typeof event === 'object' && event.step === "verifying-now")) {
      this.viewAutorityVerification = false;
      this.viewAccountInformation = false;
      this.viewVerifyingNow = true;
    }
  }

  saveFiles(event: any) {
    const { files } = event;
    if (files && files.length > 0) {
      this.selectedFile = files[0];
      console.log('FILE SELECTED:', this.selectedFile);
    } else if (files instanceof File) {
      this.selectedFile = files;
      console.log('FILE SELECTED:', this.selectedFile);
    }
  }

  deleteFile() {
    this.selectedFile = null;
    this.uploadProgress = undefined;
    console.log('FILE REMOVED');
  }

  get verificationStatus(): string {
    return this.account?.verification?.status || 'not-submitted';
  }

  handleSave() {
    if (!this.account || !this.profile) {
      console.error('Missing account or profile data');
      const data: FuseConfirmationConfig = {
        title: "Missing Information",
        message: "Account or profile information is missing. Please try again.",
        icon: {
          show: false,
        },
        actions: {
          confirm: {
            show: true,
            label: "Ok",
            color: "primary",
          },
          cancel: {
            show: false,
          },
        },
      };
      this._fuseConfirmationService.open(data);
      return;
    }

    if (!this.selectedFile) {
      const data: FuseConfirmationConfig = {
        title: "No File Selected",
        message: "Please select a file to upload.",
        icon: {
          show: false,
        },
        actions: {
          confirm: {
            show: true,
            label: "Ok",
            color: "primary",
          },
          cancel: {
            show: false,
          },
        },
      };
      this._fuseConfirmationService.open(data);
      return;
    }

    this.disableConfirm = true;

    this.authorityVerificationFileService
      .uploadAndRequestVerification(
        this.selectedFile!,
        this.account,
        this.profile,
        (progress) => {
          this.uploadProgress = progress;
        }
      )
      .pipe(
        takeUntil(this.unsubscribeAll),
        switchMap((response) => {
          const currentAccount = this.accountService.currentAccount.getValue();
          if (currentAccount && currentAccount.uuid === this.account.uuid) {
            if (!currentAccount.verification) {
              currentAccount.verification = {};
            }
            currentAccount.verification.status = 'pending';
            this.accountService.currentAccount.next(currentAccount);
          }
          if (!this.account.verification) {
            this.account.verification = {};
          }
          this.account.verification.status = 'pending';
          return of(response);
        }),
        catchError((error) => {
          const data: FuseConfirmationConfig = {
            title: "Document upload failed",
            message: "Something went wrong uploading the document. Please try again.",
            icon: {
              show: false,
            },
            actions: {
              confirm: {
                show: true,
                label: "Ok",
                color: "primary",
              },
              cancel: {
                show: false,
              },
            },
          };
          this._fuseConfirmationService.open(data);
          throw error;
        }),
        finalize(() => {
          this.disableConfirm = false;
          this.selectedFile = null;
          this.uploadProgress = undefined;
        }),
        takeUntil(this.unsubscribeAll)
      )
      .subscribe({
        error: (error) => {
          if (error.message?.includes('Firebase login')) {
            console.error('Firebase login failed:', error);
          }
        }
      });
  }
}
